import React, { forwardRef, useMemo, useRef, useEffect, useCallback, useImperativeHandle } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import './RichTextEditor.css'
import { selectMediaFile } from '../../utils/richTextEditorUpload'
import { toast, Notification } from 'components/ui'

// Constants
const MAX_IMAGE_SIZE_KB = 64
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_KB * 1024
const MAX_CONTENT_LENGTH = 1500
const MAX_IMAGE_COUNT = 1
const BASE64_OVERHEAD_MULTIPLIER = 1.33
const DATA_URL_MAX_LENGTH = Math.round(MAX_IMAGE_SIZE_BYTES * BASE64_OVERHEAD_MULTIPLIER)
const IMAGE_LOAD_TIMEOUT = 5000
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp']

// Helper Functions
const createNotification = (title, type, message) => (
	<Notification title={title} type={type} closable>
		{message}
	</Notification>
)

const showError = (title, message) => {
	toast.push(createNotification(title, 'danger', message))
}

const showWarning = (title, message) => {
	toast.push(createNotification(title, 'warning', message))
}

const getEditorInstance = (quillRef) => {
	return quillRef.current?.getEditor() || null
}

const getImageCount = (htmlContent) => {
	if (!htmlContent) return 0
	const tempDiv = document.createElement('div')
	tempDiv.innerHTML = htmlContent
	return tempDiv.querySelectorAll('img').length
}

const calculateContentLength = (html) => {
	if (!html) return 0
	
	const tempDiv = document.createElement('div')
	tempDiv.innerHTML = html
	const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim()
	const imageCount = getImageCount(html)
	
	return textContent.length + imageCount
}

const validateImageCount = (htmlContent) => {
	const imageCount = getImageCount(htmlContent)
	if (imageCount >= MAX_IMAGE_COUNT) {
		showError(
			'Image Limit Reached',
			'Only 1 image is allowed. Please remove the existing image before adding a new one.'
		)
		return false
	}
	return true
}

const validateFileType = (file) => {
	if (!file?.type?.startsWith('image/')) {
		showError('Invalid File', 'Please select a valid image file.')
		return false
	}
	return true
}

const validateFileSize = (file) => {
	if (!file || typeof file.size !== 'number' || file.size === undefined) {
		showError('Invalid File', 'File size could not be determined. Please select a valid image file.')
		return false
	}

	if (file.size > MAX_IMAGE_SIZE_BYTES) {
		showError(
			'File Too Large',
			`Image is larger than ${MAX_IMAGE_SIZE_KB}KB (${(file.size / 1024).toFixed(1)}KB). Maximum image size is ${MAX_IMAGE_SIZE_KB}KB. Please use a smaller image.`
		)
		return false
	}
	return true
}

const validateContentLength = (currentLength) => {
	const newLength = currentLength + 1 // Each image counts as 1 character
	if (newLength > MAX_CONTENT_LENGTH) {
		showError(
			'Limit Exceeded',
			`Adding this image would exceed the ${MAX_CONTENT_LENGTH} character limit. Please remove some content.`
		)
		return false
	}
	return true
}

const formatErrorMessage = (errorMessage, currentLength, maxLength) => {
	const isSizeError = errorMessage.includes('32KB') || 
		errorMessage.includes('64KB') || 
		errorMessage.includes('too large')
	const isLimitError = currentLength >= maxLength
	
	if (isLimitError && !isSizeError) {
		return 'Not enough space in signature field. Total limit is 1500 characters. Please remove some content first.'
	}
	if (isSizeError) {
		return `Image is too large even after compression. Maximum image size is ${MAX_IMAGE_SIZE_KB}KB. Please use a smaller image.`
	}
	return errorMessage
}

const insertImageIntoEditor = (quill, imageUrl, range = null) => {
	const insertIndex = range?.index ?? 0
	quill.insertEmbed(insertIndex, 'image', imageUrl, 'user')
	if (range) {
		quill.setSelection(insertIndex + 1)
	}
}

const compressImageForUpload = async (file, availableSpace = null) => {
	const { compressImageToDataUrl } = await import('../../utils/richTextEditorUpload')
	return await compressImageToDataUrl(file, DATA_URL_MAX_LENGTH, availableSpace)
}

const convertDataUrlToFile = async (dataUrl, fileName, fileType) => {
	// Convert data URL directly to Blob without using fetch (to avoid CSP issues)
	if (dataUrl.startsWith('data:')) {
		try {
			// Parse data URL: data:image/jpeg;base64,/9j/4AAQ...
			const [header, base64Data] = dataUrl.split(',')
			
			if (!base64Data) {
				throw new Error('Invalid data URL format: missing base64 data')
			}

			// Extract MIME type from data URL header if not provided
			let mimeType = fileType || 'image/jpeg'
			if (header) {
				const mimeMatch = header.match(/data:([^;]+)/)
				if (mimeMatch && mimeMatch[1]) {
					mimeType = mimeMatch[1]
				}
			}

			// Decode base64 to binary string
			let binaryString
			try {
				binaryString = atob(base64Data)
			} catch (error) {
				throw new Error('Invalid base64 data: ' + error.message)
			}

			// Convert binary string to Uint8Array
			const bytes = new Uint8Array(binaryString.length)
			for (let i = 0; i < binaryString.length; i++) {
				bytes[i] = binaryString.charCodeAt(i)
			}
			
			// Create Blob and File
			const blob = new Blob([bytes], { type: mimeType })
			const file = new File([blob], fileName || 'image.jpg', { type: mimeType })
			
			return file
		} catch (error) {
			console.error('Error converting data URL to File:', error)
			throw new Error(`Failed to convert data URL to File: ${error.message}`)
		}
	}
	
	// For non-data URLs (shouldn't happen in our case, but handle it)
	try {
		const response = await fetch(dataUrl)
		const blob = await response.blob()
		return new File([blob], fileName, { type: fileType })
	} catch (error) {
		console.error('Error fetching non-data URL:', error)
		throw new Error(`Failed to fetch URL: ${error.message}`)
	}
}

const uploadImageToAzure = async (file, uploadType) => {
	const { uploadMediaToAzure } = await import('../../utils/richTextEditorUpload')
	return await uploadMediaToAzure(file, uploadType)
}

const validateImageUrl = (imageUrl) => {
	if (!imageUrl || typeof imageUrl !== 'string') {
		showError('Invalid URL', 'Please provide a valid image URL.')
		return { valid: false, error: 'Invalid URL' }
	}

	const isDataUrl = imageUrl.startsWith('data:image/')
	const isHttpUrl = imageUrl.startsWith('http://') || imageUrl.startsWith('https://')
	const hasImageExtension = IMAGE_EXTENSIONS.some(ext => imageUrl.toLowerCase().includes(ext))

	if (!isDataUrl && !isHttpUrl && !hasImageExtension) {
		showError('Invalid Image URL', 'Please provide a valid image URL (http/https or data URL).')
		return { valid: false, error: 'Invalid image URL format' }
	}

	return { valid: true, isDataUrl, isHttpUrl }
}

const verifyImageAccessibility = async (imageUrl) => {
	return new Promise((resolve, reject) => {
		const img = new Image()
		const timeout = setTimeout(() => {
			reject(new Error('Image load timeout'))
		}, IMAGE_LOAD_TIMEOUT)

		img.onload = () => {
			clearTimeout(timeout)
			resolve()
		}
		img.onerror = (error) => {
			clearTimeout(timeout)
			reject(error || new Error('Failed to load image'))
		}
		img.src = imageUrl
	})
}

const extractImageFromClipboard = (clipboardData) => {
	const items = clipboardData?.items || []
	for (let i = 0; i < items.length; i++) {
		const item = items[i]
		if (item.type.indexOf('image') !== -1) {
			return item.getAsFile()
		}
	}
	return null
}

const RichTextEditor = forwardRef((props, ref) => {
	const {
		value,
		onChange,
		placeholder = 'Type here...',
		readOnly = false,
		theme = 'snow',
		maxLength,
		onUploadStart,
		onUploadComplete,
		onValidationError,
		uploadType,
		ignoreOnChange = false,
		...restProps
	} = props

	const quillRef = useRef(null)
	const previousValidContentRef = useRef(value || '')
	const validationErrorRef = useRef(null)
	const isInitializingRef = useRef(true)

	const getQuill = useCallback(() => getEditorInstance(quillRef), [])

	// Validate compressed file size from data URL
	const validateCompressedFileSize = useCallback((dataUrl) => {
		if (!dataUrl) return false
		
		// Calculate approximate size from base64 data URL
		const base64Data = dataUrl.split(',')[1] || ''
		const base64Length = base64Data.length
		// Base64 encoding: actual size ≈ (base64_length * 3/4)
		const paddingCount = (base64Data.match(/=/g) || []).length
		const approximateSize = Math.floor((base64Length * 3) / 4) - paddingCount
		
		if (approximateSize > MAX_IMAGE_SIZE_BYTES) {
			showError(
				'File Too Large',
				`Compressed image is still larger than ${MAX_IMAGE_SIZE_KB}KB (approximately ${(approximateSize / 1024).toFixed(1)}KB). Please use a smaller image.`
			)
			return false
		}
		return true
	}, [])

	// Process and insert image after compression/upload
	const processAndInsertImage = useCallback(async (quill, file, currentContent) => {
		try {
			// Double-check file size before processing (defense in depth)
			if (!file || file.size > MAX_IMAGE_SIZE_BYTES) {
				showError(
					'File Too Large',
					`Image is larger than ${MAX_IMAGE_SIZE_KB}KB (${(file?.size / 1024 || 0).toFixed(1)}KB). Maximum image size is ${MAX_IMAGE_SIZE_KB}KB. Please use a smaller image.`
				)
				onUploadComplete?.({ success: false, error: 'File size exceeds limit' })
				return { success: false, error: 'File size exceeds limit' }
			}

			onUploadStart?.()

			const compressResult = await compressImageForUpload(file)
			
			if (!compressResult.success || !compressResult.dataUrl) {
				const errorMessage = compressResult.error || 'Failed to compress image'
				const currentLength = calculateContentLength(currentContent)
				const displayMessage = formatErrorMessage(errorMessage, currentLength, MAX_CONTENT_LENGTH)
				
				showError('Image Upload Failed', displayMessage)
				onUploadComplete?.({ success: false, error: compressResult.error })
				return { success: false, error: compressResult.error }
			}

			// Validate compressed file size before proceeding
			if (!validateCompressedFileSize(compressResult.dataUrl)) {
				onUploadComplete?.({ success: false, error: 'Compressed file size exceeds limit' })
				return { success: false, error: 'Compressed file size exceeds limit' }
			}

			let imageUrl = compressResult.dataUrl
			let result = { success: true, url: imageUrl }

			// Upload to Azure if uploadType is provided
			if (uploadType) {
				let compressedFile
				try {
					compressedFile = await convertDataUrlToFile(
						compressResult.dataUrl,
						file.name,
						file.type
					)
				} catch (conversionError) {
					console.error('Error converting data URL to File:', conversionError)
					showError(
						'Image Processing Failed',
						`Failed to process image: ${conversionError.message || 'Unknown error'}. Please try again.`
					)
					onUploadComplete?.({ success: false, error: conversionError.message })
					return { success: false, error: conversionError.message }
				}
				
				// Validate compressed file size before upload
				if (compressedFile.size > MAX_IMAGE_SIZE_BYTES) {
					showError(
						'File Too Large',
						`Compressed image is still larger than ${MAX_IMAGE_SIZE_KB}KB (${(compressedFile.size / 1024).toFixed(1)}KB). Please use a smaller image.`
					)
					onUploadComplete?.({ success: false, error: 'Compressed file size exceeds limit' })
					return { success: false, error: 'Compressed file size exceeds limit' }
				}

				const uploadResult = await uploadImageToAzure(compressedFile, uploadType)

				if (!uploadResult.success || !uploadResult.url) {
					showError(
						'Image Upload Failed',
						uploadResult.error || 'Failed to upload image to Azure. Please try again.'
					)
					onUploadComplete?.({ success: false, error: uploadResult.error })
					return { success: false }
				}

				imageUrl = uploadResult.url
				result = { success: true, url: uploadResult.url, path: uploadResult.path }
			}

			// Insert image into editor
			const range = quill.getSelection(true)
			insertImageIntoEditor(quill, imageUrl, range)
			
			onUploadComplete?.({ success: true, url: imageUrl, ...result })
			return result
		} catch (error) {
			console.error('Error processing image:', error)
			showError('Image Upload Failed', error.message || 'Failed to upload image. Please try again.')
			onUploadComplete?.({ success: false, error: error.message })
			return { success: false, error: error.message }
		}
	}, [onUploadStart, onUploadComplete, uploadType, validateCompressedFileSize])

	// Validate and process image file
	const validateAndProcessImage = useCallback(async (file, currentContent) => {
		// Validate file type first
		if (!file) {
			return { success: false, error: 'No file provided' }
		}

		if (!validateFileType(file)) {
			onUploadComplete?.({ success: false, error: 'Invalid file type' })
			return { success: false, error: 'Invalid file type' }
		}

		// Validate file size - strict check
		if (!validateFileSize(file)) {
			onUploadComplete?.({ success: false, error: 'File size exceeds limit' })
			return { success: false, error: 'File size exceeds limit' }
		}

		const currentLength = calculateContentLength(currentContent)
		if (!validateContentLength(currentLength)) {
			onUploadComplete?.({ success: false, error: 'Total character limit exceeded' })
			return { success: false, error: 'Total character limit exceeded' }
		}

		const quill = getQuill()
		if (!quill) {
			return { success: false, error: 'Editor not available' }
		}

		return await processAndInsertImage(quill, file, currentContent)
	}, [getQuill, processAndInsertImage, onUploadComplete])

	// Custom image handler for Azure upload
	const handleImageUpload = useCallback(async () => {
		if (readOnly) return

		const quill = getQuill()
		if (!quill) return

		const currentContent = quill.root.innerHTML || ''
		
		if (!validateImageCount(currentContent)) {
			return
		}

		const file = await selectMediaFile('image/*')
		if (!file) return

		const result = await validateAndProcessImage(file, currentContent)
		if (!result?.success) {
			// Validation or processing failed - image should not be inserted
			console.warn('Image upload failed:', result?.error || 'Unknown error')
		}
	}, [readOnly, getQuill, validateAndProcessImage])

	// Handle clipboard image paste with validation
	const handlePasteImage = useCallback(async (e) => {
		if (readOnly) return

		const quill = getQuill()
		if (!quill) return

		const imageFile = extractImageFromClipboard(e.clipboardData)
		if (!imageFile) return

		e.preventDefault()

		const currentContent = quill.root.innerHTML || ''
		
		if (!validateImageCount(currentContent)) {
			return
		}

		const result = await validateAndProcessImage(imageFile, currentContent)
		if (!result?.success) {
			// Validation or processing failed - image should not be inserted
			console.warn('Image paste failed:', result?.error || 'Unknown error')
		}
	}, [readOnly, getQuill, validateAndProcessImage])

	// Insert image from URL with validation
	const insertImageFromUrl = useCallback(async (imageUrl) => {
		if (readOnly) {
			showWarning('Read Only', 'Cannot insert image in read-only mode.')
			return { success: false, error: 'Read-only mode' }
		}

		const quill = getQuill()
		if (!quill) {
			return { success: false, error: 'Editor not available' }
		}

		const urlValidation = validateImageUrl(imageUrl)
		if (!urlValidation.valid) {
			return { success: false, error: urlValidation.error }
		}

		try {
			const currentContent = quill.root.innerHTML || ''
			
			if (!validateImageCount(currentContent)) {
				return { success: false, error: 'Image limit reached' }
			}

			const currentLength = calculateContentLength(currentContent)
			if (!validateContentLength(currentLength)) {
				return { success: false, error: 'Total character limit exceeded' }
			}

			// Verify HTTP/HTTPS URL accessibility
			if (urlValidation.isHttpUrl) {
				try {
					await verifyImageAccessibility(imageUrl)
				} catch (error) {
					showError(
						'Image Load Failed',
						'Unable to load image from the provided URL. Please check if the URL is correct and accessible.'
					)
					return { success: false, error: 'Image URL not accessible' }
				}
			}

			onUploadStart?.()

			const range = quill.getSelection(true)
			insertImageIntoEditor(quill, imageUrl, range)

			onUploadComplete?.({ success: true, url: imageUrl })
			return { success: true, url: imageUrl }
		} catch (error) {
			console.error('Error inserting image from URL:', error)
			showError('Error', error.message || 'Failed to insert image. Please try again.')
			onUploadComplete?.({ success: false, error: error.message })
			return { success: false, error: error.message }
		}
	}, [readOnly, getQuill, onUploadStart, onUploadComplete])

	// Toolbar configuration
	const modules = useMemo(() => ({
		toolbar: {
			container: [
				['bold', 'italic', 'underline'],
				[{ 'color': [] }, { 'background': [] }],
				[{ 'list': 'ordered' }, { 'list': 'bullet' }],
				[{ 'indent': '-1' }, { 'indent': '+1' }],
				['blockquote'],
				[{ 'header': [1, 2, 3, 4, 5, 6, false] }],
				[{ 'size': ['small', false, 'large', 'huge'] }],
				[{ 'font': [] }],
				[{ 'script': 'sub' }, { 'script': 'super' }],
				[{ 'direction': 'rtl' }],
				['link', 'image'],
				['code-block']
			],
			handlers: {
				image: handleImageUpload
			}
		},
		clipboard: {
			matchVisual: false,
		},
		history: {
			delay: 1000,
			maxStack: 50,
			userOnly: true
		}
	}), [handleImageUpload])

	const formats = [
		'header',
		'bold', 'italic', 'underline', 'strike',
		'color', 'background',
		'align',
		'list', 'bullet',
		'indent',
		'blockquote', 'code-block',
		'link', 'image',
		'script',
		'direction',
		'size',
		'font'
	]

	// Validate content for image count and other rules
	const validateContent = useCallback((content) => {
		if (!content) return { valid: true, error: null }

		const imageCount = getImageCount(content)
		if (imageCount > MAX_IMAGE_COUNT) {
			return {
				valid: false,
				error: 'Only 1 image is allowed. Please remove extra images.'
			}
		}

		if (maxLength) {
			const contentLength = calculateContentLength(content)
			if (contentLength > maxLength) {
				return {
					valid: false,
					error: `Content exceeds the maximum length of ${maxLength} characters.`
				}
			}
		}

		return { valid: true, error: null }
	}, [maxLength])

	const handleChange = (content, delta, source, editor) => {
		if (!onChange) return

		const normalizedContent = content || ''
		const normalizedPrevious = previousValidContentRef.current || ''

		// Ignore onChange if explicitly requested by parent (e.g., during remount)
		if (ignoreOnChange) {
			// Only update ref, don't call onChange
			if (normalizedContent !== normalizedPrevious) {
				previousValidContentRef.current = normalizedContent
			}
			return
		}

		// Ignore onChange during initialization or if content hasn't actually changed
		if (isInitializingRef.current) {
			// Only update ref, don't call onChange
			if (normalizedContent !== normalizedPrevious) {
				previousValidContentRef.current = normalizedContent
			}
			return
		}

		// Ignore if content is the same as previous (prevents unnecessary onChange calls)
		if (normalizedContent === normalizedPrevious && source !== 'user') {
			return
		}

		if (source === 'user') {
			const validation = validateContent(content)
			
			if (!validation.valid) {
				validationErrorRef.current = validation.error
				onValidationError?.(validation.error)
				showError('Validation Error', validation.error)
				
				// Restore previous valid content
				const quill = getQuill()
				if (quill) {
					quill.clipboard.dangerouslyPasteHTML(previousValidContentRef.current, 'silent')
				}
				return
			}
		}

		// Clear validation error if content is valid
		validationErrorRef.current = null
		onValidationError?.(null)
		previousValidContentRef.current = normalizedContent
		onChange(normalizedContent)

		if (restProps.onChangeText) {
			const text = editor.getText()
			restProps.onChangeText(text)
		}
	}

	// Update previous valid content when value prop changes
	useEffect(() => {
		if (value !== undefined) {
			previousValidContentRef.current = value || ''
		}
	}, [value])

	// Mark initialization as complete after component mounts
	useEffect(() => {
		// Set a timeout to mark initialization as complete
		// This prevents onChange from being called during initial mount
		const timer = setTimeout(() => {
			isInitializingRef.current = false
		}, 500)
		
		return () => clearTimeout(timer)
	}, [])

	// Update readOnly state when prop changes
	useEffect(() => {
		const quill = getQuill()
		if (quill) {
			quill.enable(!readOnly)
		}
	}, [readOnly, getQuill])

	// Attach paste event listener for image validation
	useEffect(() => {
		const quill = getQuill()
		if (!quill) return

		const editorElement = quill.root
		if (!editorElement) return

		editorElement.addEventListener('paste', handlePasteImage)
		return () => {
			editorElement.removeEventListener('paste', handlePasteImage)
		}
	}, [handlePasteImage, getQuill])

	// Expose validation function and image insertion via ref
	useImperativeHandle(ref, () => ({
		validate: () => {
			const quill = getQuill()
			if (!quill) return { valid: true, error: null }

			const currentContent = quill.root.innerHTML || ''
			const validation = validateContent(currentContent)

			if (!validation.valid) {
				showError('Validation Error', validation.error)
			}

			return validation
		},
		insertImageFromUrl: (imageUrl) => insertImageFromUrl(imageUrl),
		getEditor: () => getQuill(),
		getValidationError: () => validationErrorRef.current
	}), [validateContent, insertImageFromUrl, getQuill])

	const combinedRef = (node) => {
		quillRef.current = node
	}

	return (
		<div className="rich-text-editor">
			<ReactQuill
				ref={combinedRef}
				theme={theme}
				value={value || ''}
				onChange={handleChange}
				modules={modules}
				formats={formats}
				placeholder={placeholder}
				readOnly={readOnly}
				{...restProps}
			/>
		</div>
	)
})

RichTextEditor.displayName = 'RichTextEditor'

export default RichTextEditor
